import styled from '@emotion/styled';
import { Row } from 'react-bootstrap';
import { fontSizes, fontWeights } from 'app/typography';
import { breakpoints } from 'app/spacing';

const lgBreakpoint = `@media(min-width: ${breakpoints.lg}px)`;

export const StyledLabel = styled.label(
  {
    cursor: 'pointer',
    textAlign: 'center',
    'div.child,div.child.chartType': {
      ':hover': {
        'p,svg': {
          color: 'var(--color-button-secondary-hover)',
        },
      },
    },
    '&.shrink-gutter': {
      paddingRight: 5,
      paddingLeft: 5,
      ':first-child': {
        paddingLeft: 15,
      },
      ':last-child': {
        paddingRight: 15,
      },
    },
  },
  ({ variant }) => {
    const variants = {
      content: {
        input: {
          display: 'none',
        },
        'div.child': {
          border: '1px solid',
          borderRadius: '4px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem 2rem',
          height: '100%',
          img: {
            width: '100%',
          },
          'svg,div[value="pipeline"]>div': {
            height: '25px',
            width: '25px',
          },
          'span,p,h1,h2,h3,h4,h5,h6': {
            marginTop: '1rem',
            fontSize: fontSizes.text,
          },
        },

        'div.child.chartType': {
          padding: 5,
          p: {
            color: 'var(--color-font-primary)',
            fontWeight: fontWeights.bold,
            fontSize: fontSizes.small,
            marginBottom: 0,
            marginTop: 5,
          },
          [lgBreakpoint]: {
            padding: '15px',
            p: {
              marginTop: '15px',
              fontSize: fontSizes.text,
            },
          },
          ':hover': {
            borderColor: 'var(--color-button-secondary-hover)',
            'p,svg': {
              color: 'unset',
            },
          },
        },

        '.select-box': {
          padding: 5,
          p: {
            color: 'var(--color-font-primary)',
            fontWeight: fontWeights.bold,
          },
        },
      },
      button: {
        padding: '0',
        marginRight: '15px',
        input: {
          display: 'none',
        },
        'div.child': {
          border: '1px solid',
          borderColor: 'var(--color-button-secondary-default)',
          borderRadius: '8px',
          padding: '1.1rem 1rem 0.9rem',
          letterSpacing: '0.5px',

          p: {
            color: 'var(--color-button-secondary-default)',
            fontWeight: fontWeights.bold,
            textTransform: 'uppercase',
            fontSize: fontSizes.button,
          },
        },
      },
      radio: {
        display: 'flex',
        input: {
          borderRadius: '100%',
          marginRight: '0.5rem',
          outline: 'none',
          boxShadow: '0px 1px 2px black',
        },
      },
    };
    return variants[variant] || variants.radio;
  },
  ({ checked, variant }) => {
    if (checked) {
      if (variant === 'button') {
        return {
          'div.child': {
            backgroundColor: 'var(--color-button-secondary-default)',
            p: {
              color: 'white',
            },

            ':hover': {
              backgroundColor: 'var(--color-button-secondary-hover)',
              p: {
                color: 'white',
              },
            },
          },
        };
      }

      if (variant === 'select-box') {
        return {
          'div.child': {
            div: {
              'p, svg': {
                color: 'var(--color-background-darker)',
              },
            },
            ':hover': {
              div: {
                'p, svg': {
                  color: 'var(--color-background-darker)',
                },
              },
              '.selected': {
                'p, svg': {
                  color: 'var(--color-button-secondary-hover)',
                },
              },
            },
            '.selected': {
              'p, svg': {
                color: 'var(--color-button-secondary-default)',
              },
            },
          },

          '.select-button': {
            backgroundColor: 'var(--color-button-secondary-default)',
            color: 'white',
            ':hover': {
              backgroundColor: 'var(--color-button-secondary-hover)',
              color: 'white',
            },
          },
        };
      }

      if (variant === 'content') {
        return {
          'div.child, div.child.chartType': {
            color: 'var(--color-viz-cat-2-primary)',
            'p,svg': {
              color: 'var(--color-viz-cat-2-primary)',
            },
          },
        };
      }

      return {
        'div.child, div.child.chartType': {
          color: 'var(--color-button-secondary-default)',
          'p,svg': {
            color: 'var(--color-button-secondary-default)',
          },
        },
      };
    } else {
      if (variant === 'select-box') {
        return {
          'div.child': {
            div: {
              'p, svg': {
                color: 'var(--color-background-darker)',
              },
            },
            ':hover': {
              div: {
                'p, svg': {
                  color: 'var(--color-button-secondary-hover)',
                },
              },
            },
          },

          '.select-button': {
            color: 'var(--color-button-secondary-default)',
            ':hover': {
              color: 'var(--color-button-secondary-hover)',
            },
          },
        };
      }
      return {
        'div.child': {
          borderColor:
            variant === 'button'
              ? 'var(--color-button-secondary-default)'
              : 'var(--color-border-dark)',

          'p,svg': {
            color:
              variant === 'button'
                ? 'var(--color-button-secondary-default)'
                : 'var(--color-background-dark)',
          },
        },
      };
    }
  }
);

export const StyledRow = styled(Row)(
  {
    marginBottom: '2rem',
  },
  ({ variant, noMargin }) => {
    const variants = {
      content: {},
      button: {},
      radio: {},
    };
    const style = variants[variant] || variants.radio;

    if (noMargin) {
      style.margin = 0;
    }

    return style;
  }
);
